// "Your first member" — the complete first run as one continuous worked
// example: create a calc project, add a floor joist, read the verdict, size
// the section, print the record. Every number in this article is imported
// from the same fixture module the figure captures render
// (calcPreviewFixtures), so the prose and the pixels cannot diverge.
import type { HelpArticle } from "../../types";
import {
  AUTOSIZE_PICK,
  FJ1_CHECKS,
  FJ1_GOVERNING,
  FJ1_MIN_BEARING_IN,
  FJ1_REACTION_LB,
  MAPLE_PROJECT,
  PICKER_FAIL_2X10,
  PICKER_PASS_2X12,
} from "../../../calculations/calcPreviewFixtures";
import { NDS_ROLES } from "../../../calculations/ndsRoles";

// Deterministic formatting (no toLocaleString: the committed search index
// must not depend on the build machine's locale).
const grp = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const pct = (u: number): string => `${Math.round(u * 100)}%`;

const FJ = NDS_ROLES.floor_joist.defaults;
const fjLoads = FJ.loads ?? [];
const deadPsf = fjLoads.find((l) => l.type === "D")?.mag ?? 0;
const livePsf = fjLoads.find((l) => l.type === "L")?.mag ?? 0;

export const yourFirstMemberArticle: HelpArticle = {
  id: "your-first-member",
  title: "Your first member",
  description:
    "The whole first run on one floor joist: create a calculation project, read the Design summary verdict, autosize a failing section, and print the record.",
  tags: ["first calculation", "autosize", "floor joist", "verdict", "start here"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "This is the whole first run on one member: a calculation project, a floor joist sheet, a verdict you can read, a failing section, and the record that prints. The worked example is the Maple St. Residence demo project, and every number below is the number the figures show.",
    },

    { kind: "heading", level: 2, text: "Create the project", id: "create-project" },
    { kind: "where", path: ["Calculations", "New calculation project"] },
    {
      kind: "paragraph",
      text: "A calculation project is the report container: its register lists every sheet, and its identity prints on every cover. The form asks for four things — project name (required), address or job reference, description, and the **default design method**, ASD or LRFD with ASD preselected. Every sheet inherits the project method; any single sheet can override it later in its Design Conditions card without touching the rest of the register.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "design_method" },
    {
      kind: "paragraph",
      text: "The rest of the report identity — client, job number, engineer of record, license, building code, risk category — lives in **Report details**, one page per project. Fill it whenever you like; it only has to exist by the time you print.",
    },

    { kind: "heading", level: 2, text: "New calculation", id: "new-calculation" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "New calculation"] },
    {
      kind: "paragraph",
      text: "A new project opens on its empty register; **New calculation** opens the catalog. Member role presets come first, grouped **Beams / Walls / Columns / Tension**, then the schema-driven structural calculators (steel, concrete, masonry, connections, analysis) and the load calculations. The search box filters preset cards by label and note.",
    },
    {
      kind: "paragraph",
      text: "Pick **Floor joist**. The card's note — `repetitive on · L/360 LL` — is the preset's promise: the sheet arrives configured as a spaced, sheathed floor member. Marks auto-number by role prefix (FJ, R, B, H, RB, ST, P, BC, T), so the first floor joist is `FJ-1`. One click creates the sheet and opens its workspace.",
    },
    {
      kind: "shot",
      shotId: "first-member-catalog",
      caption: "The calculation catalog: role presets first, calculators and load calcs below.",
    },
    {
      kind: "values",
      caption: "What the Floor joist preset seeds. All of it is editable on the sheet.",
      rows: [
        { label: "Section", value: `${FJ.size} ${FJ.grade} · ${FJ.species} (Database)` },
        { label: "Clear span", value: `${FJ.span} ft` },
        { label: "Spacing o.c.", value: `${FJ.spacing} in` },
        { label: "Bearing length", value: `${FJ.bearing} in` },
        { label: "Dead load", value: `D ${deadPsf} psf` },
        { label: "Floor live load", value: `L ${livePsf} psf` },
        { label: "Deflection limits", value: `L/${FJ.deflLL} live · L/${FJ.deflTL} total` },
        { label: "Repetitive member", value: "Cr on" },
      ],
    },

    { kind: "heading", level: 2, text: "The workspace", id: "workspace" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "FJ-1"] },
    {
      kind: "paragraph",
      text: "The workspace is two panes. The left pane is input, in three cards: **A. Key properties** — section source and size, role-gated geometry, supports and bearing, compression-edge bracing; **B. Loads** — the applied-load preview, one row per load with its ASCE type letter, self-weight, live-load patterning; **C. Design conditions** — design method, service and temperature, deflection criteria, the Cr and Ci toggles, and reference-value overrides. The right pane is the record: the result exactly as it prints.",
    },
    {
      kind: "paragraph",
      text: "Above both panes sits the header strip: the **mark**, the **member role** master control (switching among same-calculator roles re-seeds that preset and keeps the mark), one utilization chip per check, and the governing status pill. `Quick / Detailed` sets view density — Quick collapses card C and the right pane's detail accordions, Detailed is the default. `Lock` puts the input pane in read-only presentation mode.",
    },
    {
      kind: "shot",
      shotId: "first-member-workspace",
      caption: "FJ-1: inputs on the left, the printable record on the right.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "spacing_in" },

    { kind: "heading", level: 2, text: "Type, then leave the field", id: "autocalc" },
    {
      kind: "paragraph",
      text: "The sheet recomputes on commit, never on keystroke. The commit gate has exactly four rules:",
    },
    {
      kind: "list",
      style: "unordered",
      items: [
        "A typed number or text field commits when you **leave it** — blur, or `Enter`.",
        "A select or a toggle chip commits **on change**; the choice is the commit.",
        "Load rows commit **immediately** on add, duplicate, edit-save, and remove.",
        "**Recalculate** in the header commits whatever is pending right now.",
      ],
    },
    {
      kind: "paragraph",
      text: "So the working rhythm is: type, then leave the field. Tab through span, spacing, and a load magnitude, and the engine runs once per committed edit, never once per digit.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "While the engine runs",
      body: "The header actions read `Working…` and the right pane holds the last computed record until the new one lands. A half-typed span is never half-computed.",
    },

    { kind: "heading", level: 2, text: "Reading the first result", id: "first-result" },
    {
      kind: "paragraph",
      text: `With the preset committed, FJ-1 comes back **Pass · ${pct(FJ1_GOVERNING.utilization)}**. The Design summary pairs the section figure with one card per check; each card carries the utilization, demand over capacity in the check's own symbols, the governing load combination, and the NDS reference behind the check.`,
    },
    {
      kind: "values",
      caption: "FJ-1's five checks as the Design summary shows them.",
      rows: FJ1_CHECKS.map((check) => ({
        label: check.label,
        value: `${pct(check.utilization)} · ${check.demandSymbol} ${grp(check.demand)} / ${check.capacitySymbol} ${grp(check.capacity)} ${check.units} · ${check.combo}`,
      })),
    },
    {
      kind: "paragraph",
      text: `Verdicts band at fixed thresholds: **PASS** below 90%, **NEAR** from 90% up to 100%, **FAIL** above 100%. NEAR is a pass carrying a flag — the record prints "Governing utilization is in the near-limit band (≥ 90%)" so a reviewer reads the margin, not just the color. FJ-1's governing check is live-load deflection: 0.41 in against the L/${FJ.deflLL} allowance of 0.57 in, ${pct(FJ1_GOVERNING.utilization)}.`,
    },
    {
      kind: "shot",
      shotId: "first-member-summary",
      caption: "The Design summary: section figure, check cards, support reactions.",
    },
    {
      kind: "paragraph",
      text: `Below the checks, the support reactions: ${grp(FJ1_REACTION_LB)} lb at each end, each with the minimum bearing length the engine derives — ${FJ1_MIN_BEARING_IN} in here, against the ${FJ.bearing} in entered. These reactions are also what downstream sheets can link to (see Linking reactions).`,
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "repetitive" },

    { kind: "heading", level: 2, text: "Autosize and the picker", id: "autosize" },
    {
      kind: "paragraph",
      text: "**Select…** in card A opens the member database, scored live: every candidate row runs through the same engine against FJ-1's committed geometry and loads, and shows its per-check DCRs plus the governing one. Rows sort by governing utilization; the color bands are the verdict bands (green under 90%, amber to 100%, red over). `Passing only` hides failures, and `Include all sizes` lifts the sweep past the role's default 2x4–2x12 scope. The section currently on the sheet is flagged `Current`.",
    },
    {
      kind: "shot",
      shotId: "first-member-picker",
      caption: "The member database narrowed to 2x10s: the current row flagged at 91%, the Autosize pick at 96%, the failing No. 1 at 104%.",
    },
    {
      kind: "values",
      caption: "Three rows from that table, and what each one teaches.",
      rows: [
        {
          label: `${PICKER_FAIL_2X10.size} ${PICKER_FAIL_2X10.grade} · ${PICKER_FAIL_2X10.species}`,
          value: `${pct(PICKER_FAIL_2X10.maxDcr)} — FAIL, flexure governs`,
        },
        {
          label: `${AUTOSIZE_PICK.size} ${AUTOSIZE_PICK.grade} · ${AUTOSIZE_PICK.species}`,
          value: `${pct(AUTOSIZE_PICK.maxDcr)} — passes, inside the NEAR band (the Autosize pick)`,
        },
        {
          label: `${PICKER_PASS_2X12.size} ${PICKER_PASS_2X12.grade} · ${PICKER_PASS_2X12.species}`,
          value: `${pct(PICKER_PASS_2X12.maxDcr)} — passes with margin`,
        },
      ],
    },
    {
      kind: "paragraph",
      text: `**Autosize** answers one narrow question: the lightest catalog section that passes these loads. On this joist that is the ${AUTOSIZE_PICK.size} ${AUTOSIZE_PICK.grade} at ${pct(AUTOSIZE_PICK.maxDcr)} — passing, and inside the NEAR band, which is what "lightest" costs. The toast names the pick (\`Autosized to ${AUTOSIZE_PICK.size} ${AUTOSIZE_PICK.grade} · ${AUTOSIZE_PICK.species}\`), the section seats, and the sheet recomputes. When ${pct(AUTOSIZE_PICK.maxDcr)} is thinner than you want to stamp, the table has already priced the margin: one row down, the ${PICKER_PASS_2X12.size} ${PICKER_PASS_2X12.grade} carries the same loads at ${pct(PICKER_PASS_2X12.maxDcr)}.`,
    },
    {
      kind: "paragraph",
      text: "Autosize sweeps sawn lumber only. Glulam, SCL, and I-joist candidates sit in the same picker behind family tabs, but they are a deliberate pick: choosing one seats its catalog key and dispatches the matching calculator (the sheet retargets on recompute).",
    },

    { kind: "heading", level: 2, text: "Where the record prints", id: "print-export" },
    {
      kind: "paragraph",
      text: "The right pane is the record — printing it adds a cover block that stays hidden on screen: calculation type and mark, then the project identity from Report details. A blank field is omitted, never invented.",
    },
    {
      kind: "values",
      caption: "The demo project's cover identity, as it prints on FJ-1.",
      rows: [
        { label: "Project", value: MAPLE_PROJECT.name },
        { label: "Client", value: MAPLE_PROJECT.client },
        { label: "Job #", value: MAPLE_PROJECT.jobNumber },
        { label: "Engineer", value: MAPLE_PROJECT.engineer },
        { label: "License", value: MAPLE_PROJECT.license },
        { label: "Building code", value: MAPLE_PROJECT.buildingCode },
        { label: "Risk category", value: MAPLE_PROJECT.riskCategory },
      ],
    },
    {
      kind: "paragraph",
      text: "**Export** in the header opens the package wizard: Simplified or Detailed format and the sheet selection; at project scope it adds a project cover and a calculation index in register order. A preflight review lists every selected sheet's status before anything generates, and the PDF preserves engine statuses — a missing result stays missing rather than getting filled.",
    },
    {
      kind: "shot",
      shotId: "first-member-export",
      caption: "The export wizard's format step, opened from FJ-1's header.",
    },

    { kind: "related", topics: ["member-sheets", "linking-reactions", "fastening-escalation"] },
  ],
};
